import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import GameTile from '../../gameTile/containers/GameTile';
import HoverGameTile from '../../gameTile/components/HoverGameTile';
import ScrollBar from '../components/ScrollerBar';
import gamesPageLayout from '../constants/layoutConstants';
import carouselService from '../services/carouselService';

function CarouselContainer({
  gamesData,
  loadMore,
  name,
  sortApiParams,
  pageContext,
  sortPosition,
  sortStyle,
  isInitializedLoading,
  ...props
}) {
  const carouselWindowRef = useRef();
  const carouselScrollRef = useRef();
  const gameTileRef = useRef();

  const [carouselWindowWidth, setCarouselWindowWidth] = useState(
    carouselWindowRef.current?.clientWidth
  );

  const [carouselScrollOffset, setCarouselScrollOffset] = useState(
    carouselScrollRef.current?.offsetLeft ? carouselScrollRef.current?.offsetLeft : 0
  );
  const [gameTileWidth, setGameTileWidth] = useState(
    carouselService.getGameTileWidth(gameTileRef.current)
  );
  const [numGamesFitted, setNumGamesFitted] = useState(
    carouselService.getNumOfGamesFitted(carouselWindowWidth, gameTileWidth)
  );

  const [carouselStyle, setCarouselStyle] = useState(gamesPageLayout.carouselScrollStartPoint);
  const updateCarouselStyle = offset => {
    setCarouselStyle({
      left: `${offset}px`
    });
  };

  const [scrollStatus, setScrollState] = useState({
    isPrevDisabled: true,
    isNextDisabled: false
  });
  const { leftScroller, rightScroller, leftScrollIcon, rightScrollIcon } = gamesPageLayout;
  const [scrollClasses, setScrollClasses] = useState({
    prev: leftScroller,
    next: rightScroller
  });

  const updateScrollerLayout = (isStartDisabled, isEndDisabled) => {
    setScrollState({
      isPrevDisabled: isStartDisabled,
      isNextDisabled: isEndDisabled
    });
    const leftScrollClasses = classNames(leftScroller, { disabled: isStartDisabled });
    const rightScrollClasses = classNames(rightScroller, { disabled: isEndDisabled });
    setScrollClasses({
      prev: leftScrollClasses,
      next: rightScrollClasses
    });
  };

  const updatePrevAndNextScrollStatus = useCallback(
    offset => {
      const isStartPoint = carouselService.isStartPoint(offset);
      const currentCarsouselScrollWidth = carouselScrollRef.current?.clientWidth;
      let currentCarouselWindowWidth = carouselWindowWidth;
      if (!carouselWindowWidth) {
        currentCarouselWindowWidth = carouselWindowRef.current?.clientWidth;
      }
      const isScrollingEnded = carouselService.isScrollingEnded(
        currentCarsouselScrollWidth,
        currentCarouselWindowWidth,
        offset
      );

      updateScrollerLayout(isStartPoint, isScrollingEnded);
    },
    [carouselWindowWidth, updateScrollerLayout]
  );

  const [cursor, setCursor] = useState(0);
  const [rawData, setRawData] = useState(gamesData);
  const [startRenderingIdx, setStartRenderingIdx] = useState(0);
  const [lastRenderingIdx, setLastRenderingIdx] = useState(20);
  const [renderingData, setRenderingData] = useState([]);

  let timeoutForScrollToPrev = null;
  const scrollToPrev = () => {
    if (!scrollStatus.isPrevDisabled) {
      let currentGameTileWidth = gameTileWidth;
      if (!currentGameTileWidth || Number.isNaN(currentGameTileWidth)) {
        currentGameTileWidth = carouselService.getGameTileWidth(gameTileRef.current);
        setGameTileWidth(currentGameTileWidth);
      }
      const offset = carouselService.scrollPrev(
        carouselScrollOffset,
        carouselWindowWidth,
        gameTileWidth
      );
      setCarouselScrollOffset(offset);
      updateCarouselStyle(offset);
      updatePrevAndNextScrollStatus(offset);

      const previousIdx = cursor - numGamesFitted;
      if (previousIdx <= startRenderingIdx) {
        const currentData = renderingData;
        const potentialStartRenderingIdx =
          startRenderingIdx - gamesPageLayout.numberOfGameRendering;
        const updatedStartRenderingIdx =
          potentialStartRenderingIdx < 0 ? 0 : potentialStartRenderingIdx;
        const updatedLastRenderingIdx = cursor;
        currentData.forEach((each, index) => {
          if (index > cursor) {
            currentData[index] = {};
          }
          if (index <= cursor && index >= updatedStartRenderingIdx) {
            currentData[index] = rawData[index];
          }
        });
        setStartRenderingIdx(updatedStartRenderingIdx);
        setLastRenderingIdx(updatedLastRenderingIdx);
        setRenderingData(currentData);
      }
      setCursor(previousIdx);
    }
  };

  const onScrollToPrev = () => {
    if (!timeoutForScrollToPrev) {
      scrollToPrev();
      timeoutForScrollToPrev = setTimeout(() => {
        clearTimeout(timeoutForScrollToPrev);
      }, 200);
    }

    return false;
  };

  const processRenderingWindowsForNext = (currentRenderingData, currentRawData) => {
    const currentData = currentRenderingData;
    const potentialLastRenderingIdx = lastRenderingIdx + gamesPageLayout.numberOfGameRendering;
    const updateLastRenderingIdx =
      potentialLastRenderingIdx > currentRawData?.length
        ? currentRawData?.length
        : potentialLastRenderingIdx;
    currentData.forEach((each, index) => {
      if (index < cursor) {
        currentData[index] = {};
      }
      if (index >= cursor && index < updateLastRenderingIdx) {
        currentData[index] = currentRawData[index];
      }
    });
    setStartRenderingIdx(cursor);
    setLastRenderingIdx(updateLastRenderingIdx);
    setRenderingData(currentData);
  };

  let timeoutForScrollToNext = null;
  const scrollToNext = useCallback(isNative => {
    if (!scrollStatus.isNextDisabled) {
      loadMore();
      if (!isNative) {
        let currentGameTileWidth = gameTileWidth;
        if (!currentGameTileWidth || Number.isNaN(currentGameTileWidth)) {
          currentGameTileWidth = carouselService.getGameTileWidth(gameTileRef.current);
          setGameTileWidth(currentGameTileWidth);
        }
        const offset = carouselService.scrollNext(
          carouselScrollOffset,
          carouselWindowWidth,
          currentGameTileWidth
        );
        setCarouselScrollOffset(offset);
        updateCarouselStyle(offset);
        updatePrevAndNextScrollStatus(offset);

        const endIdx = cursor + numGamesFitted;

        const predictEndIdx = cursor + numGamesFitted * 2;
        if (predictEndIdx >= lastRenderingIdx) {
          processRenderingWindowsForNext(renderingData, rawData);
        }
        setCursor(endIdx);
      }
    }
  });

  const onScrollToNext = () => {
    if (!timeoutForScrollToNext) {
      scrollToNext();
      timeoutForScrollToNext = setTimeout(() => {
        clearTimeout(timeoutForScrollToNext);
      }, 200);
    }
    return false;
  };

  useEffect(() => {
    const currentGameTileWidth = carouselService.getGameTileWidth(gameTileRef.current);
    if (!Number.isNaN(currentGameTileWidth)) {
      setGameTileWidth(() => currentGameTileWidth);

      const currentNumGamesFitted = carouselService.getNumOfGamesFitted(
        carouselWindowWidth,
        currentGameTileWidth
      );
      setNumGamesFitted(currentNumGamesFitted);
    }
  }, [carouselWindowWidth]);

  useEffect(() => {
    function handleResize() {
      setCarouselWindowWidth(carouselWindowRef.current?.clientWidth);
    }

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(
    () => {
      let isSubscribed = true;
      if (isSubscribed) {
        if (isInitializedLoading) {
          setCarouselWindowWidth(carouselWindowRef.current?.clientWidth);
          setCarouselScrollOffset(carouselScrollRef.current?.offsetLeft);
        } else {
          // if rendering data is getting update, need to update rendering windows
          const predictEndIdx = cursor + numGamesFitted * 2;
          if (predictEndIdx >= lastRenderingIdx) {
            processRenderingWindowsForNext(renderingData, rawData);
          }
        }
        updatePrevAndNextScrollStatus(carouselScrollOffset);
      }
      return () => {
        isSubscribed = false;
      };
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [renderingData]
  );

  useEffect(
    () => {
      let isSubscribed = true;
      if (isSubscribed && gamesData?.length) {
        setRawData(gamesData);

        if (!renderingData.length) {
          setRenderingData(currentData => {
            const updatedData = currentData.concat(gamesData);
            return updatedData;
          });
          setLastRenderingIdx(gamesData.length);
        } else {
          const updatedGamesNum = gamesData.length;
          const currentGamesNum = renderingData.length;
          if (updatedGamesNum > currentGamesNum) {
            const appendingArray = Array(updatedGamesNum - currentGamesNum).fill({});
            setRenderingData(currentData => {
              const updatedData = currentData.concat(appendingArray);
              return updatedData;
            });
          }
        }
      }
      return () => {
        isSubscribed = false;
      };
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [gamesData]
  );
  // listen native scrolling event, happening on touch screen which native scrolling turned on
  const listenScrollEvent = useCallback(() => {
    console.log('------listenScrollEvent------');
    scrollToNext(true);
  }, [scrollToNext]);

  return (
    <div
      data-testid='game-carousel-carousel-container'
      className='clearfix horizontal-scroll-window'
      ref={carouselWindowRef}>
      <div
        className='horizontally-scrollable'
        style={carouselStyle}
        onScroll={listenScrollEvent}
        ref={carouselScrollRef}>
        <ul className='hlist games game-cards game-tile-list'>
          {renderingData.length > 0 &&
            renderingData.map((game, index) => {
              const keyValue = `${game.universeId}${index}`;
              const isFirstTile = index === cursor;
              const isLastTile = index === cursor + numGamesFitted - 1;
              const isVisibleTile = index >= cursor && index <= cursor + numGamesFitted - 1;
              return sortStyle === gamesPageLayout.sortStyle.featureGameTile ? (
                <li
                  className={classNames(
                    'list-item hover-game-tile',
                    { 'first-tile': isFirstTile },
                    { 'last-tile': isLastTile },
                    { 'visible-tile': isVisibleTile }
                  )}
                  id={game.universeId}
                  key={keyValue}>
                  {game?.universeId && (
                    <HoverGameTile
                      game={game}
                      isVisible={isVisibleTile}
                      pageType={gamesPageLayout.pageType.gameCarousel}
                      ref={gameTileRef}
                      index={index}
                      sortName={name}
                      pageContext={pageContext}
                      sortPosition={sortPosition}
                      {...props}
                    />
                  )}
                </li>
              ) : (
                <li
                  className='list-item game-card game-tile'
                  id={game.universeId}
                  key={keyValue}
                  title={game?.name}>
                  {game?.universeId && (
                    <GameTile
                      game={game}
                      isHidden={!(index >= cursor && index <= cursor + numGamesFitted - 1)}
                      pageType={gamesPageLayout.pageType.gameCarousel}
                      ref={gameTileRef}
                      index={index}
                      sortName={name}
                      pageContext={pageContext}
                      sortPosition={sortPosition}
                      {...props}
                    />
                  )}
                </li>
              );
            })}
        </ul>
      </div>

      <ScrollBar
        scrollClassNames={scrollClasses.prev}
        scrollIconClassName={leftScrollIcon}
        isDisabled={scrollStatus.isPrevDisabled}
        scroll={onScrollToPrev}
        {...props}
      />
      <ScrollBar
        scrollClassNames={scrollClasses.next}
        scrollIconClassName={rightScrollIcon}
        isDisabled={scrollStatus.isNextDisabled}
        scroll={onScrollToNext}
        {...props}
      />
    </div>
  );
}

CarouselContainer.propTypes = {
  gamesData: PropTypes.arrayOf(PropTypes.shape({ universeId: PropTypes.number })).isRequired,
  loadMore: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  sortApiParams: PropTypes.shape({ hasMoreRows: PropTypes.bool }).isRequired,
  pageContext: PropTypes.shape({ pageId: PropTypes.string }).isRequired,
  sortPosition: PropTypes.number.isRequired,
  sortStyle: PropTypes.string.isRequired,
  isInitializedLoading: PropTypes.bool.isRequired,
  translate: PropTypes.func.isRequired
};

export default CarouselContainer;
